import React, { useMemo, useState } from 'react';
import { RadarChart } from '../charts/RadarChart';
import { ChartData } from 'chart.js';
import { TeamAnalysis } from '../../types/team';
import { Persona } from '../../types/persona';

interface TeamCapabilityChartProps {
  teamAnalysis: TeamAnalysis;
  personas?: Persona[];
}

export const TeamCapabilityChart: React.FC<TeamCapabilityChartProps> = ({
  teamAnalysis,
  personas = []
}) => {
  const [showIndividualMembers, setShowIndividualMembers] = useState(false);
  
  const chartData = useMemo(() => {
    const labels = Object.keys(teamAnalysis.capabilities).map(
      cap => cap.charAt(0).toUpperCase() + cap.slice(1)
    );
    
    const datasets = [
      {
        label: 'Team Average',
        data: Object.values(teamAnalysis.capabilities),
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        pointBackgroundColor: 'rgb(54, 162, 235)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(54, 162, 235)'
      }
    ];
    
    // Add individual persona datasets if requested
    if (showIndividualMembers && personas.length > 0) {
      const personaDatasets = personas.map((persona, index) => {
        // Generate a color based on index
        const hue = (index * 137) % 360; // Use golden angle to spread colors
        
        return {
          label: persona.name,
          data: labels.map(label => 
            persona.capabilities[label.toLowerCase()] || 0
          ),
          backgroundColor: `hsla(${hue}, 70%, 60%, 0.2)`,
          borderColor: `hsl(${hue}, 70%, 50%)`,
          pointBackgroundColor: `hsl(${hue}, 70%, 50%)`,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: `hsl(${hue}, 70%, 50%)`,
          hidden: index > 2 // Hide by default if more than 3 personas
        };
      });
      
      datasets.push(...personaDatasets);
    }
    
    const data: ChartData<'radar'> = {
      labels,
      datasets
    };
    
    return data;
  }, [teamAnalysis, personas, showIndividualMembers]);
  
  return (
    <div className="team-capability-chart">
      <h3 className="text-lg font-medium mb-4">Team Capabilities</h3>
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <RadarChart 
          data={chartData} 
          height={400}
          width={400}
          options={{
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 12
                }
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.dataset.label}: ${context.raw}/10`;
                  }
                }
              }
            }
          }}
        />
        
        {personas.length > 0 && (
          <div className="mt-4 flex justify-end">
            <button
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setShowIndividualMembers(!showIndividualMembers)}
            >
              {showIndividualMembers ? 'Hide' : 'Show'} Individual Members
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
